import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useChildTokens } from './useChildTokens';

/**
 * DailyMoodCheck — "Hur mår du just nu?" (prototypens .mood-check).
 *
 * KLIENT-LOKAL: backend saknar fristående dags-humör-endpoint (underlag 🔴), så
 * valet persisteras i localStorage per dag (nyckel child_mood_YYYY-MM-DD). Ingen
 * mock, ingen backend-anrop. Vid val ges en kort bekräftelse via onPicked så att
 * kompanjonen kan reagera (happy + tack-bubbla).
 */

const MOODS: ReadonlyArray<{ emoji: string; key: string }> = [
  { emoji: '😄', key: 'great' },
  { emoji: '🙂', key: 'good' },
  { emoji: '😐', key: 'ok' },
  { emoji: '😔', key: 'sad' },
  { emoji: '😤', key: 'angry' },
];

const storageKey = (date: Date) => `child_mood_${format(date, 'yyyy-MM-dd')}`;

type DailyMoodCheckProps = {
  /** Anropas när barnet väljer ett humör (kompanjonen kan reagera). */
  onPicked?: (moodKey: string) => void;
};

const DailyMoodCheck = ({ onPicked }: DailyMoodCheckProps) => {
  const { t } = useTranslation();
  const tokens = useChildTokens();
  const today = storageKey(new Date());

  const [selected, setSelected] = useState<string | null>(() => {
    try {
      return localStorage.getItem(today);
    } catch {
      return null;
    }
  });

  const pick = useCallback(
    (moodKey: string) => {
      setSelected(moodKey);
      try {
        // Klient-lokal persistens (ingen backend för dags-humör).
        localStorage.setItem(today, moodKey);
      } catch {
        // localStorage kan saknas (privat läge) — valet är ändå satt i state.
      }
      onPicked?.(moodKey);
    },
    [today, onPicked],
  );

  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: tokens.textSoft, mb: 0.75 }}>
        {t('child.mood.prompt')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {MOODS.map(({ emoji, key }) => {
          const isSelected = selected === key;
          return (
            <Box
              key={key}
              component="button"
              type="button"
              onClick={() => pick(key)}
              aria-label={t(`child.mood.${key}`) as string}
              aria-pressed={isSelected}
              sx={{
                fontSize: '1.5rem',
                lineHeight: 1,
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: isSelected ? 'primary.main' : tokens.border,
                bgcolor: isSelected ? tokens.primaryLight : tokens.card,
                cursor: 'pointer',
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                transition: 'transform 0.15s, border-color 0.15s',
                '@media (hover: hover)': {
                  '&:hover': { transform: 'scale(1.2)', borderColor: 'primary.main' },
                },
              }}
            >
              {emoji}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default DailyMoodCheck;
